import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { EChartOption, ECharts } from 'echarts';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CommonService } from '../../service/common.service';
import { BasicBoxPlotService } from './basic-box-plot.service';
import { ChartOptionService } from '../../service/chart-option.service';
import { ChartResizeService } from '../../service/chart-resize.service';

@Component({
  selector: 'bcac-basic-box-plot',
  templateUrl: './basic-box-plot.component.html',
  styleUrls: ['./basic-box-plot.component.scss']
})
export class BasicBoxPlotComponent implements OnInit {

  constructor(
    private commonService: CommonService,
    private basicBoxPlotService: BasicBoxPlotService,
    private chartOptionService: ChartOptionService,
    private chartResizeService: ChartResizeService
  ) { }

  chartTitle = {
    show: true,
    label: "图表标题"
  };

  customToolbox = {
    show: true
  }

  // 布局设置
  cardPadding = "1rem";
  cardBgColor = "#fff";
  chartTitleColor = "";

  // echart图表元素
  chartBackgroundColor: string = "#FFF";
  chartColors: string[] = this.chartOptionService.getChartColors('default');
  chartTitles = this.chartOptionService.getChartTitle();
  chartLegend = this.chartOptionService.getChartLegend();
  chartGrid: EChartOption.Grid = this.commonService.changeFontSize().interval;
  chartxAxis: EChartOption.XAxis[] = this.basicBoxPlotService.getChartxAxis();
  chartyAxis: EChartOption.YAxis[] = this.chartOptionService.getChartyAxis(1);
  chartTextStyle: EChartOption.TextStyle = this.chartOptionService.getTextStyle();
  boxPlotSeries: EChartOption.SeriesBoxplot = this.basicBoxPlotService.getDefaultBoxPlotSeries();
  scatterSeries: EChartOption.Series = this.basicBoxPlotService.getDefaultScatterSeries();
  chartTooltip: EChartOption.Tooltip = this.basicBoxPlotService.getBoxPlotTooltip();
  chartSeriesList: EChartOption.Series[] = [];


  chartOption = {
    backgroundColor: this.chartBackgroundColor,
    title: this.chartTitles,
    legend: this.chartLegend,
    grid: this.chartGrid,
    xAxis: this.chartxAxis,
    yAxis: this.chartyAxis,
    series: this.chartSeriesList,
    tooltip: this.chartTooltip,
    color: this.chartColors,
    textStyle: this.chartTextStyle
  };

  initOpts = this.chartOptionService.getInitOpts();
  optionInit: ECharts;
  onOptionInit(ec) {
    this.optionInit = ec;
  }

  // X轴刻度间隔
  intervalData = [];
  intervalSelectedData;
  intervalSelectedDataChange(e: any) {
    // 跟新图表interval
    this.basicBoxPlotService.setInterval(this.chartOption, e);
    this.updateOption();
  }

  axisScaleChange(e: any) {
    this.basicBoxPlotService.setInterval(this.chartOption, e);
    this.updateOption();
  }

  // 接口区
  @Input()
  set bcacDarkMode(darkMode: boolean) {
    if (darkMode == true) {
      this.basicBoxPlotService.changeChartTitleDark(this, darkMode);
      this.basicBoxPlotService.chartBackgroundColorDarkMode(this.chartOption);
      this.basicBoxPlotService.xAxisDarkMode(this.chartxAxis);
      this.basicBoxPlotService.yAxisDarkMode(this.chartyAxis);
      this.basicBoxPlotService.colorsDarkMode(this.chartColors);
      this.basicBoxPlotService.legendDarkMode(this.chartLegend);
      this.basicBoxPlotService.seriesDarkMode(this.boxPlotSeries);
    }
  }

  @Input()
  set bcacSmallMode(smallMode: boolean) {
    if (smallMode) {
      this.basicBoxPlotService.setSmallModeGrid(this.chartGrid);
      this.basicBoxPlotService.setSmallModePadding(this)
    }
  }

  legendPosition = "bottom";
  @Input()
  set bcacLegendPosition(legendPosition: string) {
    this.legendPosition = legendPosition;
    this.basicBoxPlotService.setLegendPosition(this.chartGrid, this.chartLegend, legendPosition);
  }

  @Input()
  set bcacTitleSwitch(titleSwitch: boolean) {
    this.chartTitle.show = titleSwitch;
  }

  @Input()
  set bcacTitle(title: string) {
    this.chartTitle.label = title;
  }

  @Input()
  set bcacUnit(unit: string) {
    this.basicBoxPlotService.setTooltipUnit(this.chartTooltip, unit);
  }

  @Input()
  set bcacXaxisName(xAxisName: string) {
    this.basicBoxPlotService.setXaxisName(this.chartxAxis, xAxisName);
  }

  @Input()
  set bcacYaxisName(yAxisNames: string) {
    this.basicBoxPlotService.setYaxisName(this.chartyAxis, yAxisNames);
  }

  @Input()
  set bcacYnameGap(yNameGap: number) {
    this.basicBoxPlotService.setYNameGap(this.chartyAxis, yNameGap);
  }

  @Input()
  set bcacColors(colorList: string[]) {
    this.basicBoxPlotService.chartColorsChange(this.chartColors, colorList)
  }

  // @Input()
  // set bcacAreaStyleSwitch(areaStyleSwitch: boolean) {
  //   if (areaStyleSwitch) {
  //     this.boxPlotSeries['areaStyle'] = {};
  //   }
  // }

  @Input()
  set bcacIntervalData(value: any[]) {
    this.intervalData = value;
  }

  // 加载等待
  _bcacChartLoading: boolean = false
  @Input()
  set bcacChartLoading(loading: boolean) {
    this._bcacChartLoading = loading;
  }

  legendDataList;
  @Input()
  set bcacChartData(chartData: any) {
    let legendDataList = [];
    if (chartData.hasOwnProperty('seriesName')) {
      chartData.seriesName.forEach(element => {
        legendDataList.push(
          {
            label: element,
            value: element,
            checked: true
          }
        )
      });
    }
    this.legendDataList = legendDataList;
    this.basicBoxPlotService.buildSeries(chartData, this.chartSeriesList, this.chartxAxis, this.boxPlotSeries, this.scatterSeries)
    this.updateOption();
  }

  @Input()
  set bcacXAxisLabelRotate(xAxisLabelRotate) {
    this.basicBoxPlotService.setXAxisLabelRotate(this.chartxAxis, xAxisLabelRotate)
  }

  dataSourceSelectData = [];
  @Input()
  set bcacDataSourceList(value: any[]) {
    this.dataSourceSelectData = value;
  }

  legendChange($event) {
    this.basicBoxPlotService.topLegendChange($event, this.optionInit);
  }

  @Input()
  set bcacDimensionNames(dimensionNames) {
    this.basicBoxPlotService.setSeriesDimensionNames(this.boxPlotSeries, dimensionNames)
  }

  @Input()
  set bcacYAxisMin(yAxisMin: number | undefined | null) {
    this.basicBoxPlotService.setYAxisMin(this.chartyAxis, yAxisMin);
  }

  @Input()
  set bcacYAxisMax(yAxisMax: number | undefined | null) {
    this.basicBoxPlotService.setYAxisMax(this.chartyAxis, yAxisMax);
  }

  @Input()
  set bcacYAxisInterval(yAxisInterval: number) {
    this.basicBoxPlotService.setYAxisInterval(this.chartyAxis, yAxisInterval)
  }

  @Output()
  bcacDataSourceSelectEmit = new EventEmitter<any>();
  dataSourceChange(e: any) {
    this.bcacDataSourceSelectEmit.emit(e);
  }
  options;
  // echart配置项
  initEchart() {
    this.options = this.chartOption;
  }

  updateOption() {
    if (this.optionInit) {
      this.optionInit.setOption(this.options, true)
    }
  }

  ngOnInit() {
    this.initEchart();
    this.updateOption();
    fromEvent(window, 'resize').pipe(debounceTime(200)).subscribe(() => {
      this.chartResizeService.autoResizeAdapt(this.optionInit, this.options)
    })
  }

}
